import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { canPlayMp4OnCanvas, playMp4OnCanvas } from './mp4CanvasPlayer';
import './HeroVideo.css';

const DEFAULT_VIDEO = '/videos/hero-drone.mp4';
const INTRO_MS = 4000;
const DEFAULT_LINES = ['Persönlich.', 'Kompetent.', 'Fair.'];

function writerLines(lines) {
  const incoming = (lines || []).map((line) => String(line).trim()).filter(Boolean);
  if (incoming.length >= 2) return incoming;
  const one = incoming[0] || DEFAULT_LINES.join(' ');
  const dotted = one.split('.').map((part) => part.trim()).filter(Boolean);
  if (dotted.length >= 2) return dotted.map((part) => `${part}.`);
  return incoming.length ? incoming : DEFAULT_LINES;
}

function useTypewriter(lines, active) {
  const [text, setText] = useState(lines[0] || '');
  const key = lines.join('\n');

  useEffect(() => {
    if (!active || lines.length === 0) return undefined;
    let line = 0;
    let index = lines.length > 1 ? 0 : lines[0].length;
    let deleting = false;
    let timer;
    setText(lines.length > 1 ? '' : lines[0]);

    const tick = () => {
      const full = lines[line] || '';
      if (!deleting) {
        index += 1;
        setText(full.slice(0, index));
        if (index >= full.length) {
          if (lines.length < 2) return;
          deleting = true;
          timer = window.setTimeout(tick, 1600);
          return;
        }
        timer = window.setTimeout(tick, 70);
        return;
      }
      index -= 1;
      setText(full.slice(0, Math.max(index, 0)));
      if (index <= 0) {
        deleting = false;
        line = (line + 1) % lines.length;
        timer = window.setTimeout(tick, 320);
        return;
      }
      timer = window.setTimeout(tick, 36);
    };

    timer = window.setTimeout(tick, lines.length > 1 ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [active, key, lines]);

  return text;
}

function isAppleTouch() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function setViewportHeight() {
  if (typeof window === 'undefined') return;
  const viewport = window.visualViewport;
  const height = Math.ceil(Math.max(
    window.innerHeight || 0,
    document.documentElement.clientHeight || 0,
    viewport ? viewport.height : 0,
    viewport ? viewport.height + viewport.offsetTop : 0,
    isAppleTouch() && window.screen ? window.screen.height : 0
  ));
  if (height > 0) {
    document.documentElement.style.setProperty('--spg-vh', `${height}px`);
  }
}

function unlockVideo(video) {
  if (!video) return;
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', 'true');
}

function playVideo(video) {
  if (!video) return Promise.resolve(false);
  unlockVideo(video);
  const play = video.play();
  if (play && play.then) {
    return play.then(() => true).catch(() => false);
  }
  return Promise.resolve(!video.paused);
}

function isVideoPlaying(video) {
  return Boolean(video && !video.paused && !video.ended && video.readyState > 2);
}

function preferCanvasPlayback() {
  return isAppleTouch() && canPlayMp4OnCanvas();
}

export default function HeroVideo({
  src,
  image,
  title = 'Sportplatzgarage Gutknecht',
  lines = DEFAULT_LINES,
  compact = false,
  pageTitle,
  intro = false,
  onIntroEnd,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasPlayingRef = useRef(false);
  const revealedRef = useRef(!intro || compact);
  const [revealed, setRevealed] = useState(!intro || compact);
  const [needsTap, setNeedsTap] = useState(false);
  const [useCanvas, setUseCanvas] = useState(() => preferCanvasPlayback());
  const rotating = useMemo(
    () => writerLines(lines),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(lines) ? lines.join('\n') : String(lines || '')]
  );
  const typed = useTypewriter(rotating, revealed && !compact);
  const videoSrc = src || DEFAULT_VIDEO;
  const useImage = Boolean(image);
  const playVideoHero = !compact && !useImage;

  const finishIntro = useCallback(() => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealed(true);
    setNeedsTap(false);
    if (onIntroEnd) onIntroEnd();
  }, [onIntroEnd]);

  const playFromGesture = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      unlockVideo(video);
      const play = video.play();
      if (play && play.catch) play.catch(() => {});
    }
    if (intro && !compact) finishIntro();
  }, [compact, finishIntro, intro]);

  const setVideoRef = useCallback((video) => {
    videoRef.current = video;
    if (video) {
      unlockVideo(video);
      playVideo(video);
    }
  }, []);

  useEffect(() => {
    setViewportHeight();
    const viewport = window.visualViewport;
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    if (viewport) {
      viewport.addEventListener('resize', setViewportHeight);
      viewport.addEventListener('scroll', setViewportHeight);
    }
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      if (viewport) {
        viewport.removeEventListener('resize', setViewportHeight);
        viewport.removeEventListener('scroll', setViewportHeight);
      }
    };
  }, []);

  useEffect(() => {
    if (!playVideoHero || !useCanvas) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const controller = new AbortController();
    let introDelay;
    canvasPlayingRef.current = false;
    playMp4OnCanvas(canvas, videoSrc, {
      signal: controller.signal,
      onReady: () => {
        canvasPlayingRef.current = true;
        setNeedsTap(false);
        if (intro && !compact) {
          introDelay = window.setTimeout(finishIntro, INTRO_MS);
        }
      },
      onError: () => {
        if (!controller.signal.aborted) setUseCanvas(false);
      },
    }).then((ok) => {
      if (!ok && !controller.signal.aborted) {
        setUseCanvas(false);
      }
    }).catch(() => {
      if (!controller.signal.aborted) {
        setUseCanvas(false);
        setNeedsTap(true);
      }
    });
    const failSafe = window.setTimeout(() => {
      if (!canvasPlayingRef.current && !controller.signal.aborted) {
        setUseCanvas(false);
      }
    }, 5000);
    return () => {
      controller.abort();
      window.clearTimeout(failSafe);
      if (introDelay) window.clearTimeout(introDelay);
    };
  }, [videoSrc, playVideoHero, useCanvas, intro, compact, finishIntro]);

  useEffect(() => {
    if (!playVideoHero || useCanvas) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    unlockVideo(video);

    const attempt = () => {
      playVideo(video).then((started) => {
        if (started) {
          setNeedsTap(false);
          return;
        }
        if (canPlayMp4OnCanvas()) setUseCanvas(true);
      });
    };
    const onPlaying = () => setNeedsTap(false);

    attempt();
    video.addEventListener('canplay', attempt);
    video.addEventListener('loadeddata', attempt);
    video.addEventListener('loadedmetadata', attempt);
    video.addEventListener('playing', onPlaying);
    document.addEventListener('visibilitychange', attempt);

    const retries = [120, 400, 1000].map((ms) => window.setTimeout(attempt, ms));
    const fallback = window.setTimeout(() => {
      if (!isVideoPlaying(video) && canPlayMp4OnCanvas()) setUseCanvas(true);
    }, 800);

    return () => {
      video.removeEventListener('canplay', attempt);
      video.removeEventListener('loadeddata', attempt);
      video.removeEventListener('loadedmetadata', attempt);
      video.removeEventListener('playing', onPlaying);
      document.removeEventListener('visibilitychange', attempt);
      retries.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(fallback);
    };
  }, [videoSrc, playVideoHero, useCanvas]);

  useEffect(() => {
    if (!intro || compact || useCanvas) return undefined;
    const timer = window.setTimeout(() => {
      if (isVideoPlaying(videoRef.current)) {
        finishIntro();
        return;
      }
      setNeedsTap(true);
    }, INTRO_MS);
    return () => window.clearTimeout(timer);
  }, [intro, compact, finishIntro, useCanvas]);

  return (
    <section
      className={`spg-hero ${compact ? 'is-compact' : ''} ${revealed ? 'is-revealed' : 'is-intro'}${needsTap ? ' is-waiting' : ''}`}
      onPointerDown={playFromGesture}
    >
      {useImage ? (
        <img className="spg-hero-photo" src={image} alt="" />
      ) : playVideoHero && useCanvas ? (
        <canvas ref={canvasRef} className="spg-hero-video" />
      ) : playVideoHero ? (
        <video
          ref={setVideoRef}
          className="spg-hero-video"
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
        />
      ) : null}
      <div className="spg-hero-shade" />
      {needsTap && (
        <div className="spg-hero-play" aria-hidden="true">
          <span className="spg-hero-play-btn" />
        </div>
      )}

      <div className="spg-hero-copy">
        <div className="spg-hero-kicker">Garage · Service · Diagnose</div>
        <h1>{pageTitle || title}</h1>
        {!compact && (
          <>
            <p className="spg-hero-rotator">{typed || '\u00a0'}</p>
            <div className="spg-hero-actions">
              <RouterLink className="spg-btn" to="/online-termin">Termin anfragen</RouterLink>
              <RouterLink className="spg-btn spg-btn-ghost" to="/dienstleistungen">Leistungen</RouterLink>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
